import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Download, Trash2, Loader2 } from "lucide-react";

function fmt(iso) {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Governance() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get("/audit-logs").then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, []);

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `retainproof-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete", { password: pw, confirm });
      toast.success("Firm data deleted");
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Governance</p>
        <h1 className="font-display text-4xl font-light mt-1">Audit &amp; Data</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Review account activity, export your firm's data, or permanently delete it.
        </p>
      </div>

      {/* Data controls */}
      <div className="grid md:grid-cols-2 gap-4 mb-10">
        <div className="border border-zinc-200 bg-white p-6 flex flex-col" data-testid="data-export-card">
          <h2 className="font-display text-2xl">Export your data</h2>
          <p className="text-sm text-zinc-500 mt-2 flex-1">Download a complete JSON copy of your firm, users, vendors, leads, invoices, budgets, compliance records and audit log.</p>
          <Button onClick={exportData} disabled={exporting} data-testid="export-data-button" className="mt-4 self-start bg-[#0B1F33] text-white rounded-none h-11">
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export JSON
          </Button>
        </div>

        <div className="border border-[#B54747]/40 bg-[#FCF4F4] p-6 flex flex-col" data-testid="data-delete-card">
          <h2 className="font-display text-2xl text-[#B54747]">Delete firm data</h2>
          <p className="text-sm text-zinc-600 mt-2 flex-1">Permanently erase your firm and all associated records. This cannot be undone.</p>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="delete-account-open" className="mt-4 self-start rounded-none h-11 border-[#B54747] text-[#B54747] hover:bg-[#B54747]/5">
                <Trash2 className="mr-2 h-4 w-4" /> Delete firm
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Delete firm data</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <p className="text-sm text-zinc-600">This permanently deletes your firm, all users, and all data. Enter your password and type <b>DELETE</b> to confirm.</p>
                <div>
                  <Label>Password</Label>
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="delete-password-input" className="mt-1 rounded-none" />
                </div>
                <div>
                  <Label>Type DELETE</Label>
                  <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="delete-confirm-input" className="mt-1 rounded-none" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={deleteAccount} disabled={deleting} data-testid="delete-account-confirm" className="bg-[#B54747] text-white rounded-none hover:bg-[#933a3a]">
                  {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Permanently delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Audit log */}
      <h2 className="font-display text-2xl mb-4">Activity log</h2>
      {!logs ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="border border-zinc-200 bg-white" data-testid="audit-log-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Detail</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} className="hover:bg-zinc-50" data-testid={`audit-row-${l.id}`}>
                  <TableCell className="font-mono-data text-xs text-zinc-500 whitespace-nowrap">{fmt(l.created_at)}</TableCell>
                  <TableCell className="text-sm">{l.user_email || "—"}</TableCell>
                  <TableCell><span className="font-mono-data text-xs bg-zinc-100 px-2 py-1">{l.action}</span></TableCell>
                  <TableCell className="text-sm text-zinc-600">{[l.resource, l.detail].filter(Boolean).join(" · ") || "—"}</TableCell>
                  <TableCell className="font-mono-data text-xs text-zinc-400">{l.ip || "—"}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-10" data-testid="audit-empty">No activity recorded yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
